import { useForm, Controller } from "react-hook-form";
import { Spin, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import AnimatedMainContainer from "../../../../components/AnimatedMainContainer";
import {
  ActionButton,
  Container,
  ContainerButtons,
  Horizontal,
  InputTitle,
  PreviewImage,
  UploadButton,
  RemoveImageButton,
  FieldWrapper,
  TrashIconContainer,
  PreviewContainer,
  CardTextName,
  FieldWrapperWithoutWidthFull,
  DescInputTitle,
} from "../../styles";
import { useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import { MdOutlineCancel } from "react-icons/md";
import { FaRegSave } from "react-icons/fa";
import { FaRegTrashAlt } from "react-icons/fa";
import {
  createCredentialTemplate,
  getCredentialTemplateById,
  updateCredentialTemplate,
} from "../../../../service/manage.access";
import { useToast } from "../../../../hooks/toast";
import { useParams, useHistory } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const convertPdfToImage = async (file) => {
  const buffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 2 });

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: context, viewport }).promise;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(
        new File([blob], "template.png", {
          type: "image/png",
        }),
      );
    }, "image/png");
  });
};

const getPreviewSrc = (file) => {
  if (!file) return null;
  return typeof file === "string" ? file : URL.createObjectURL(file);
};

export const CredentialTemplateForm = () => {
  const intl = useIntl();
  const [templateFile, setTemplateFile] = useState([]);
  const { token } = useSelector((state) => state.auth);
  const { addToast } = useToast();
  const { chosenEvent, chosenEventName, chosenCredential, modelId } =
    useParams();
  const history = useHistory();
  const [isLoadingtemplate, setIsLoadingtemplate] = useState(false);
  const isLoading = isLoadingtemplate;
  const [newEventid, setNewEventId] = useState(false);
  const [newCredentialType, setNewCredentialType] = useState(false);

  const schema = z.object({
    eventId: z.number().nullable(),
    credentialType: z.string().nullable(),
    file: z.any().refine(
      (val) => {
        if (!modelId) {
          return Array.isArray(val) && val.length > 0;
        }
        return true;
      },
      {
        message: intl.formatMessage({ id: "required_field" }),
      },
    ),
  });

  const {
    control,
    handleSubmit,
    setError,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      eventId: null,
      credentialType: !modelId ? chosenCredential || null : null,
      file: [],
    },
  });

  useEffect(() => {
    return () => {
      templateFile.forEach((f) => {
        if (f instanceof File) URL.revokeObjectURL(f);
      });
    };
  }, [templateFile]);

  useEffect(() => {
    if (!modelId) return;

    const fetchCredentialTemplateById = async () => {
      try {
        setIsLoadingtemplate(true);

        const response = await getCredentialTemplateById({
          token,
          eventId: chosenEvent,
          modelId,
        });
        const templateData = response.data.models[0];

        reset({
          eventId: null,
          credentialType: null,
          file: [],
        });

        if (templateData.file) {
          setTemplateFile([templateData.file]);
          setValue("file", [templateData.file]);
        }
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + error.response?.data?.code,
          }),
        });
      } finally {
        setIsLoadingtemplate(false);
      }
    };

    fetchCredentialTemplateById();
  }, [modelId]);

  const onSubmit = async (data) => {
    if (templateFile.length === 0) {
      setError("file", {
        type: "required",
        message: intl.formatMessage({ id: "required_field" }),
      });
      return;
    }

    const formData = new FormData();

    if (newEventid) {
      formData.append("eventId", Number(chosenEvent));
    }

    if (newCredentialType) {
      formData.append("credentialType", chosenCredential);
    }

    if (templateFile[0] instanceof File) {
      formData.append("file", templateFile[0]);
    }

    try {
      if (modelId) {
        await updateCredentialTemplate({
          token,
          eventId: chosenEvent,
          modelId,
          data: formData,
        });

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "success.updated_template",
          }),
        });
      } else {
        await createCredentialTemplate({
          token,
          data: formData,
          eventId: chosenEvent,
        });

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "success.register_template",
          }),
        });
      }

      reset();
      history.push(
        `/admin/credential-management/${chosenEvent}/${chosenEventName}/${chosenCredential}`,
      );
    } catch (error) {
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + error.response?.data?.code,
        }),
      });
    } finally {
      setNewEventId(false);
      setNewCredentialType(false);
    }
  };

  useEffect(() => {
    if (!modelId) setNewEventId(true);
  }, [newEventid, modelId]);

  useEffect(() => {
    if (!modelId) setNewCredentialType(true);
  }, [newCredentialType, modelId]);

  return (
    <AnimatedMainContainer
      breadcrumb={
        modelId
          ? intl.formatMessage({ id: "edit_credential_template" })
          : intl.formatMessage({ id: "register_new_credential_template" })
      }
    >
      <Spin spinning={isLoading}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Container>
            <Horizontal>
              <FieldWrapperWithoutWidthFull>
                <CardTextName>
                  {intl.formatMessage({ id: "event" })}:{" "}
                  <span>{chosenEventName}</span>
                </CardTextName>
              </FieldWrapperWithoutWidthFull>

              <FieldWrapperWithoutWidthFull>
                <CardTextName>
                  {intl.formatMessage({ id: "credential" })}:{" "}
                  <span>{intl.formatMessage({ id: chosenCredential })}</span>
                </CardTextName>
              </FieldWrapperWithoutWidthFull>
            </Horizontal>

            <FieldWrapper>
              <InputTitle>
                {intl.formatMessage({ id: "credential_template" })}
                <p>*</p>
              </InputTitle>
              <DescInputTitle>
                {intl.formatMessage({ id: "upload_file_template" })}
              </DescInputTitle>

              <Controller
                name="file"
                control={control}
                render={({ field }) => (
                  <PreviewContainer hasFile={templateFile.length > 0}>
                    {templateFile.length === 0 && (
                      <Upload
                        accept=".png,.jpg,.pdf"
                        fileList={templateFile}
                        beforeUpload={() => false}
                        showUploadList={false}
                        onChange={async ({ fileList }) => {
                          const file = fileList[0]?.originFileObj;
                          if (!file) return;

                          const finalFile =
                            file.type === "application/pdf"
                              ? await convertPdfToImage(file)
                              : file;

                          setTemplateFile([finalFile]);
                          setValue("file", [finalFile], {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          field.onChange([finalFile]);
                        }}
                      >
                        <UploadButton type="button">
                          <UploadOutlined style={{ fontSize: 44 }} />
                          {intl.formatMessage({ id: "add_template" })}
                        </UploadButton>
                      </Upload>
                    )}

                    {templateFile.length > 0 && (
                      <TrashIconContainer>
                        <RemoveImageButton
                          type="button"
                          onClick={() => {
                            setTemplateFile([]);
                            setValue("file", [], {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                            field.onChange([]);
                          }}
                        >
                          <FaRegTrashAlt style={{ fontSize: "20px" }} />
                        </RemoveImageButton>

                        <PreviewImage
                          src={getPreviewSrc(templateFile[0])}
                          alt="preview"
                        />
                      </TrashIconContainer>
                    )}
                  </PreviewContainer>
                )}
              />

              {errors.file && (
                <p style={{ color: "red" }}>{errors.file.message}</p>
              )}
            </FieldWrapper>

            <ContainerButtons>
              <ActionButton
                type="button"
                cancel
                onClick={() =>
                  history.push(
                    `/admin/credential-management/${chosenEvent}/${chosenEventName}/${chosenCredential}`,
                  )
                }
              >
                <MdOutlineCancel size={20} />
                {intl.formatMessage({ id: "cancel" })}
              </ActionButton>

              <ActionButton onClick={handleSubmit(onSubmit)} save>
                <FaRegSave />
                {intl.formatMessage({ id: "save" })}
              </ActionButton>
            </ContainerButtons>
          </Container>
        </form>
      </Spin>
    </AnimatedMainContainer>
  );
};
